<template>
  <main class="courses-page">
    <div class="courses-shell">
      <MarketingToolHeader
        eyebrow="Well come FIT Akademie"
        title="Kurse & HYROX/Reformer Programme"
        description="Entdecke aktuelle Trainingsprogramme, kommende Weiterbildungen und deine gebuchten Termine."
        icon="mdi-school-outline">
        <template v-slot:actions>
          <v-btn outlined color="#58d388" class="secondary-action" :loading="loading || programLoading" @click="refreshPage">
            <v-icon left>mdi-refresh</v-icon>
            Aktualisieren
          </v-btn>
        </template>
      </MarketingToolHeader>

      <section class="program-showcase" aria-label="HYROX/Reformer Programme">
        <div class="program-showcase-heading">
          <div>
            <span>HYROX & Reformer</span>
            <h2>HYROX/Reformer Programme</h2>
            <p>Pläne, Coaching-Hinweise und Übungsvideos zentral in der App.</p>
          </div>
          <v-btn
            v-if="canManagePrograms"
            outlined
            color="#58d388"
            class="program-manage-action"
            @click="$router.push('/employee-courses/programs/admin')">
            <v-icon left>mdi-pencil-outline</v-icon>Verwalten
          </v-btn>
        </div>

        <v-autocomplete
          v-if="canManagePrograms && programStudioOptions.length"
          v-model="programStudioFilter"
          :items="programStudioOptions"
          dark
          color="#58d388"
          :menu-props="{ dark: true }"
          item-text="name"
          item-value="_id"
          label="Nach Studio filtern"
          placeholder="Alle Studios"
          persistent-placeholder
          clearable
          outlined
          hide-details
          class="program-studio-filter" />

        <div v-if="programLoading" class="program-loading">
          <v-progress-circular indeterminate color="#58d388" size="36" width="3" />
          <span>Programme werden geladen ...</span>
        </div>

        <v-alert v-else-if="programError" type="error" text>{{ programError }}</v-alert>

        <div v-else-if="visiblePrograms.length" class="program-grid">
          <button v-for="program in visiblePrograms" :key="program._id" type="button" class="program-tile" @click="openProgram(program)">
            <span class="program-tile-heading">
              <span class="program-tile-icon"><v-icon>mdi-run-fast</v-icon></span>
              <span class="program-tile-copy">
                <small>{{ programStateLabel(program) }} · {{ programDateLabel(program.validFrom) }}</small>
                <strong>{{ program.title }}</strong>
              </span>
            </span>
            <span class="program-tile-description">{{ program.intro || 'Trainingsplan, Hinweise und Übungsunterlagen ansehen.' }}</span>
            <span class="program-studios" aria-label="Sichtbar in diesen Studios">
              <span v-if="!program.studios || !program.studios.length">Alle Studios</span>
              <span v-for="studio in program.studios" :key="studio._id">{{ studio.name }}</span>
            </span>
            <span class="program-tile-meta">
              <span><v-icon>mdi-view-grid-outline</v-icon>{{ program.blocks.length }} Inhalte</span>
              <span class="program-tile-action">Programm öffnen <v-icon>mdi-arrow-right</v-icon></span>
            </span>
          </button>
        </div>

        <div v-else class="program-empty">
          <span><v-icon>mdi-clipboard-text-outline</v-icon></span>
          <div>
            <strong>Keine HYROX/Reformer Programme verfügbar</strong>
            <p>{{ programStudioFilter ? 'Für dieses Studio sind keine Programme veröffentlicht.' : 'Sobald ein Programm für dein Studio veröffentlicht wird, erscheint es hier.' }}</p>
          </div>
          <v-btn v-if="programStudioFilter" text color="#58d388" @click="programStudioFilter = null">Alle Programme anzeigen</v-btn>
          <v-btn v-if="canManagePrograms" color="#58d388" class="black--text" depressed @click="$router.push('/employee-courses/programs/admin')">
            Programm erstellen
          </v-btn>
        </div>
      </section>

      <EmployeeCourseVideos v-if="canViewEmployeeCourses" ref="courseVideos" />

      <template v-if="canViewEmployeeCourses">
        <div class="employee-courses-heading">
          <div>
            <span>Mitarbeiterkurse</span>
            <h2>Kurse & Weiterbildungen</h2>
            <p>Termine ansehen, Plätze prüfen und deine Buchungen verwalten.</p>
          </div>
          <v-btn
            v-if="canManageEmployeeCourses"
            outlined
            color="#58d388"
            class="employee-courses-manage-action"
            @click="$router.push('/employee-courses/admin')">
            <v-icon left>mdi-pencil-outline</v-icon>Kurse verwalten
          </v-btn>
        </div>

        <section class="metric-grid" aria-label="Kursübersicht">
          <v-card dark flat class="metric-card">
            <span class="metric-icon icon-calendar"><v-icon>mdi-calendar-month-outline</v-icon></span>
            <div>
              <span>Kommende Termine</span>
              <strong>{{ courses.length }}</strong>
              <small>aktuell im Kursplan</small>
            </div>
          </v-card>

          <v-card dark flat class="metric-card">
            <span class="metric-icon icon-green"><v-icon>mdi-seat-outline</v-icon></span>
            <div>
              <span>Direkt buchbar</span>
              <strong>{{ availableCourseCount }}</strong>
              <small>mit freien Plätzen</small>
            </div>
          </v-card>

          <v-card dark flat class="metric-card">
            <span class="metric-icon"><v-icon>mdi-check-decagram-outline</v-icon></span>
            <div>
              <span>Meine Buchungen</span>
              <strong>{{ myBookingsCount }}</strong>
              <small>bestätigte Termine</small>
            </div>
          </v-card>

          <v-card dark flat class="metric-card">
            <span class="metric-icon icon-waiting"><v-icon>mdi-timer-sand</v-icon></span>
            <div>
              <span>Warteliste</span>
              <strong>{{ myWaitlistCount }}</strong>
              <small>vorgemerkte Termine</small>
            </div>
          </v-card>
        </section>

        <div class="courses-container">
        <!-- Filters Toolbar -->
        <v-card dark flat class="filters-card mb-6">
          <div class="filters-heading">
            <span class="section-icon"><v-icon>mdi-tune-variant</v-icon></span>
            <div>
              <span>Ansicht anpassen</span>
              <strong>Passende Kurse finden</strong>
            </div>
          </div>
          <v-row align="center" dense>
            <v-col cols="12" sm="5" md="4">
              <v-select
                dark
                v-model="studioFilter"
                :items="studioItems"
                label="Standort (Studio)"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-map-marker-outline"
                class="courses-filter-select"></v-select>
            </v-col>
            <v-col cols="12" sm="7" md="8" class="text-sm-right mt-3 mt-sm-0">
              <v-chip-group v-model="statusFilter" mandatory active-class="active-chip" class="status-filter-group">
                <v-chip filter value="all" small outlined>Alle Kurse</v-chip>
                <v-chip filter value="registered" small color="green" outlined>Meine Buchungen</v-chip>
                <v-chip filter value="available" small color="yellow darken-2" outlined>Freie Plätze</v-chip>
                <v-chip filter value="waitlisted" small color="orange" outlined>Warteliste</v-chip>
              </v-chip-group>
            </v-col>
          </v-row>
        </v-card>

        <!-- Calendar Strip Selector (Extremely Mobile Friendly) -->
        <div v-if="!loading && availableDays.length > 0" class="calendar-strip-wrapper mb-6">
          <div class="calendar-strip d-flex align-center gap-3">
            <!-- "All days" card option -->
            <button
              type="button"
              class="calendar-day-card d-flex flex-column align-center justify-center cursor-pointer transition-all"
              :class="{ 'active-day-card': selectedDateKey === 'all' }"
              :aria-pressed="selectedDateKey === 'all' ? 'true' : 'false'"
              @click="selectedDateKey = 'all'">
              <v-icon size="20" :color="selectedDateKey === 'all' ? '#ffffff' : '#58d388'">mdi-calendar-multiple</v-icon>
              <span class="day-weekday text-uppercase font-weight-black mt-1" :class="selectedDateKey === 'all' ? 'white--text' : 'grey--text'"
                >Alle</span
              >
            </button>

            <!-- Individual day cards -->
            <button
              v-for="day in availableDays"
              :key="day.dateString"
              type="button"
              class="calendar-day-card d-flex flex-column align-center justify-center cursor-pointer transition-all"
              :class="{ 'active-day-card': selectedDateKey === day.dateString }"
              :aria-pressed="selectedDateKey === day.dateString ? 'true' : 'false'"
              @click="selectedDateKey = day.dateString">
              <span class="day-weekday text-uppercase font-weight-bold">{{ day.dayName }}</span>
              <span class="day-num font-weight-black">{{ day.dayNum }}</span>
              <span class="day-month text-uppercase">{{ day.monthName }}</span>

              <!-- Indicators: Green for registered, Orange for waitlisted -->
              <div class="day-indicator-dots d-flex gap-1 mt-1">
                <span v-if="day.hasBooking" class="indicator-dot dot-green"></span>
                <span v-if="day.hasWaitlist" class="indicator-dot dot-orange"></span>
              </div>
            </button>
          </div>
        </div>

        <!-- Loading / Empty States -->
        <div v-if="loading" class="text-center py-12">
          <v-progress-circular indeterminate color="#58d388" size="56" class="mb-4"></v-progress-circular>
          <div class="grey--text text--lighten-1 subtitle-1">Lade verfügbare Kurse...</div>
        </div>

        <div v-else-if="groupedCourses.length === 0" class="text-center py-12 empty-state rounded-2xl">
          <v-icon size="64" color="grey darken-2" class="mb-4">mdi-calendar-blank</v-icon>
          <h3 class="text-h6 font-weight-bold white--text mb-1">Keine Kurse gefunden</h3>
          <p class="grey--text caption max-width-text mx-auto px-4">
            Für die gewählten Filter sind zurzeit keine Mitarbeiter-Lektionen geplant. Ändere die Filter oder lade die Seite neu.
          </p>
        </div>

        <!-- Grouped Courses List -->
        <div v-else>
          <div v-for="group in groupedCourses" :key="group.date" class="mb-8 width-100">
            <!-- Elegant Date Header -->
            <div class="d-flex align-center mb-4 pb-2 border-bottom-header">
              <span class="date-header-dot mr-2"></span>
              <h3 class="text-h6 font-weight-black white--text tracking-wide">{{ group.date }}</h3>
              <v-spacer></v-spacer>
              <span class="caption grey--text font-weight-bold">{{ group.courses.length }} Lektionen</span>
            </div>

            <!-- Courses Grid for this Date -->
            <v-row class="courses-grid">
              <v-col v-for="course in group.courses" :key="course._id" cols="12" md="6" lg="4" class="d-flex">
                <v-card
                  dark
                  class="course-card rounded-xl d-flex flex-column width-100 flex-grow-1"
                  :class="{ 'border-registered': isUserRegistered(course) }"
                  @click="openCourseDetails(course)">
                  <!-- Card Image/Header Accent -->
                  <div class="card-header-accent pa-4 d-flex justify-space-between align-start relative-box">
                    <v-chip small :color="getSpotsColor(course)" class="font-weight-bold black--text">
                      {{ getSpotsText(course) }}
                    </v-chip>

                    <v-chip v-if="isUserRegistered(course)" small color="green" class="font-weight-bold black--text">
                      <v-icon x-small left>mdi-check</v-icon> Gebucht
                    </v-chip>
                    <v-chip v-else-if="isUserWaitlisted(course)" small color="orange" class="font-weight-bold black--text">
                      <v-icon x-small left>mdi-clock-outline</v-icon> Warteliste (Pos. {{ getUserWaitlistPos(course) }})
                    </v-chip>
                  </div>

                  <!-- Card Info -->
                  <v-card-text class="pa-5 pt-2 flex-grow-1 d-flex flex-column">
                    <div class="d-flex align-center mb-2">
                      <v-icon x-small color="grey lighten-1" class="mr-1">mdi-map-marker</v-icon>
                      <span class="caption grey--text font-weight-bold">{{ course.studio ? course.studio.name : "WELL COME FIT" }}</span>
                    </div>

                    <h3 class="white--text font-weight-bold text-h6 mb-1 line-clamp-2" style="line-height: 1.25">
                      {{ course.format ? course.format.title : "Mitarbeiterkurs" }}
                    </h3>

                    <!-- Optional timeslot specific details -->
                    <div v-if="course.details" class="caption yellow--text text--darken-2 mb-2 font-italic font-weight-medium">
                      {{ course.details }}
                    </div>

                    <div class="divider-thin mb-3"></div>

                    <!-- Time Info -->
                    <div class="d-flex align-center mb-3">
                      <v-icon small color="#58d388" class="mr-2">mdi-clock-outline</v-icon>
                      <span class="body-2 white--text font-weight-medium">
                        {{ formatLocalTime(course.dateTime) }} Uhr <span class="grey--text">({{ course.duration }} Min)</span>
                      </span>
                    </div>

                    <!-- Abmeldefrist Alert info -->
                    <div class="mt-auto pt-3">
                      <div v-if="isDeadlinePassed(course)" class="caption red--text text--lighten-2 d-flex align-center">
                        <v-icon x-small color="red" class="mr-1">mdi-lock-outline</v-icon>
                        Abmeldefrist abgelaufen
                      </div>
                      <div v-else class="caption grey--text d-flex align-center">
                        <v-icon x-small color="grey" class="mr-1">mdi-clock-alert-outline</v-icon>
                        Abmeldung bis: {{ formatShortDateTime(course.cancellationDeadline) }}
                      </div>
                    </div>
                  </v-card-text>

                  <!-- Card Actions -->
                  <v-card-actions class="pa-4 pt-0">
                    <v-btn
                      v-if="isUserRegistered(course) || isUserWaitlisted(course)"
                      block
                      outlined
                      color="red lighten-1"
                      class="rounded-lg font-weight-bold uppercase-btn"
                      :disabled="isDeadlinePassed(course)"
                      @click.stop="confirmUnregistration(course)">
                      {{ isDeadlinePassed(course) ? "Gesperrt" : "Abmelden" }}
                    </v-btn>
                    <v-btn
                      v-else-if="hasAvailableSpots(course)"
                      block
                      color="#009640"
                      class="white--text rounded-lg font-weight-bold primary-course-action"
                      :loading="registeringId === course._id"
                      @click.stop="registerForCourse(course)">
                      Anmelden
                    </v-btn>
                    <v-btn
                      v-else-if="course.waitlistEnabled"
                      block
                      color="orange"
                      class="black--text rounded-lg font-weight-bold uppercase-btn"
                      :loading="registeringId === course._id"
                      @click.stop="registerForCourse(course)">
                      Auf Warteliste
                    </v-btn>
                    <v-btn v-else block disabled class="rounded-lg font-weight-bold uppercase-btn"> Ausgebucht </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
      </template>
    </div>

    <v-dialog
      v-model="programDialog"
      max-width="56.25rem"
      dark
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
      content-class="program-detail-dialog">
      <v-card v-if="selectedProgram" class="program-detail-card">
        <header class="program-detail-header">
          <span class="program-detail-icon"><v-icon>mdi-run-fast</v-icon></span>
          <div>
            <small>{{ programStateLabel(selectedProgram) }} · {{ programDateLabel(selectedProgram.validFrom) }}</small>
            <h2>{{ selectedProgram.title }}</h2>
          </div>
          <v-btn icon aria-label="Programm schliessen" @click="closeProgram"><v-icon>mdi-close</v-icon></v-btn>
        </header>

        <v-card-text class="program-detail-body">
          <div class="program-studios" aria-label="Sichtbar in diesen Studios">
            <span v-if="!selectedProgram.studios || !selectedProgram.studios.length">Alle Studios</span>
            <span v-for="studio in selectedProgram.studios" :key="studio._id">{{ studio.name }}</span>
          </div>
          <section v-if="selectedProgram.intro" class="program-overview">
            <span>Beschreibung</span>
            <p>{{ selectedProgram.intro }}</p>
          </section>

          <section v-if="selectedProgramContentBlocks.length" class="program-detail-section">
            <header class="program-section-heading">
              <div>
                <span class="program-section-icon"><v-icon>mdi-text-box-check-outline</v-icon></span>
                <div>
                  <small>Wichtige Informationen</small>
                  <h3>Inhalte &amp; Hinweise</h3>
                </div>
              </div>
              <span>{{ selectedProgramContentBlocks.length }}</span>
            </header>

            <div class="program-content-list">
              <article
                v-for="block in selectedProgramContentBlocks"
                :key="block._id"
                class="program-copy-block"
                :class="{ 'is-notice': block.type === 'notice' }">
                <span class="program-copy-icon">
                  <v-icon>{{ block.type === "notice" ? "mdi-lightbulb-on-outline" : "mdi-text-box-outline" }}</v-icon>
                </span>
                <div>
                  <small>{{ block.type === "notice" ? "Hinweis" : "Information" }}</small>
                  <h4 v-if="block.title">{{ block.title }}</h4>
                  <p>{{ block.content }}</p>
                </div>
              </article>
            </div>
          </section>

          <section v-if="selectedProgramMediaBlocks.length" class="program-detail-section">
            <header class="program-section-heading">
              <div>
                <span class="program-section-icon"><v-icon>mdi-folder-play-outline</v-icon></span>
                <div>
                  <small>Zum Öffnen antippen</small>
                  <h3>Dokumente</h3>
                </div>
              </div>
              <span>{{ selectedProgramMediaBlocks.length }}</span>
            </header>

            <div class="program-document-list">
              <article v-for="block in selectedProgramMediaBlocks" :key="block._id" class="program-document-row">
                <span class="program-document-icon" :class="`is-${block.type}`">
                  <v-icon>{{ block.type === "video" ? "mdi-play-circle-outline" : "mdi-image-outline" }}</v-icon>
                </span>
                <div class="program-document-copy">
                  <small>{{ programMediaLabel(block) }}</small>
                  <h4>{{ programMediaTitle(block) }}</h4>
                  <p :class="{ 'is-empty': !block.content }">{{ block.content || "Keine Beschreibung hinterlegt." }}</p>
                </div>
                <v-btn outlined color="#58d388" class="program-document-action" @click="openProgramMedia(block)">
                  <v-icon left>{{ block.type === "video" ? "mdi-play-circle-outline" : "mdi-open-in-new" }}</v-icon>
                  {{ block.type === "video" ? "Video anschauen" : "Bild öffnen" }}
                </v-btn>
              </article>
            </div>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="mediaDialog"
      max-width="56.25rem"
      dark
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
      content-class="program-media-dialog">
      <v-card v-if="selectedProgram && selectedMediaBlock" class="program-media-dialog-card">
        <header class="program-media-dialog-header">
          <span class="program-media-dialog-icon">
            <v-icon>{{ selectedMediaBlock.type === "video" ? "mdi-play-circle-outline" : "mdi-image-outline" }}</v-icon>
          </span>
          <div>
            <small>{{ programMediaLabel(selectedMediaBlock) }}</small>
            <h2>{{ programMediaTitle(selectedMediaBlock) }}</h2>
          </div>
          <div class="program-media-dialog-actions">
            <v-btn
              v-if="selectedMediaBlock.type === 'image'"
              icon
              aria-label="Bild herunterladen"
              :loading="downloadingMedia"
              :disabled="downloadingMedia"
              @click="downloadProgramImage">
              <v-icon>mdi-download-outline</v-icon>
            </v-btn>
            <v-btn icon aria-label="Dokument schliessen" @click="mediaDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </header>

        <v-card-text class="program-media-dialog-body">
          <div class="program-media-viewer" :class="`is-${selectedMediaBlock.type}`">
            <SecuredMedia
              :src="programMediaPath(selectedProgram, selectedMediaBlock)"
              :media-type="selectedMediaBlock.type"
              :stream-playback="selectedMediaBlock.type === 'video'"
              :download-name="programMediaDownloadName(selectedMediaBlock)"
              :show-app-download-action="false"
              :cache-key="`${selectedProgram._id}-${selectedMediaBlock._id}`"
              eager />
          </div>
          <div v-if="selectedMediaBlock.content" class="program-media-description">
            <span>Beschreibung</span>
            <p>{{ selectedMediaBlock.content }}</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Detailed Dialog Modal -->
    <v-dialog
      v-model="detailDialog"
      max-width="600px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition"
      class="rounded-2xl"
      dark>
      <v-card v-if="selectedCourse" class="detail-card dialog-surface pa-6 rounded-2xl">
        <div class="d-flex align-start justify-space-between mb-4">
          <div>
            <span class="caption yellow--text text--darken-2 font-weight-black text-uppercase tracking-widest"> Kursdetails & Anmeldung </span>
            <h2 class="text-h5 font-weight-black white--text italic-title mt-1" style="line-height: 1.2">
              {{ selectedCourse.format ? selectedCourse.format.title : "Mitarbeiterkurs" }}
            </h2>
            <div v-if="selectedCourse.details" class="caption yellow--text text--darken-2 font-italic font-weight-bold mt-1">
              * {{ selectedCourse.details }}
            </div>
          </div>
          <v-btn icon color="grey" @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="divider-thin mb-4"></div>

        <!-- Description -->
        <div class="mb-6">
          <h4 class="caption grey--text text-uppercase font-weight-black tracking-wider mb-2">Beschreibung</h4>
          <p class="body-2 grey--text text--lighten-2 leading-relaxed" style="white-space: pre-line">
            {{ selectedCourse.format ? selectedCourse.format.description : "Keine Beschreibung hinterlegt." }}
          </p>
        </div>

        <!-- Meta Grid -->
        <v-row class="mb-6" dense>
          <v-col cols="6" class="pa-2">
            <div class="meta-box pa-3 rounded-xl text-center">
              <v-icon color="#58d388" class="mb-1">mdi-map-marker-outline</v-icon>
              <div class="caption grey--text">Standort</div>
              <div class="body-2 font-weight-bold white--text mt-1">
                {{ selectedCourse.studio ? selectedCourse.studio.name : "WELL COME FIT" }}
              </div>
            </div>
          </v-col>

          <v-col cols="6" class="pa-2">
            <div class="meta-box pa-3 rounded-xl text-center">
              <v-icon color="#58d388" class="mb-1">mdi-account-group-outline</v-icon>
              <div class="caption grey--text">Belegung</div>
              <div class="body-2 font-weight-bold white--text mt-1">{{ selectedCourse.registeredCount }} / {{ selectedCourse.capacity }} Plätze</div>
            </div>
          </v-col>

          <v-col cols="6" class="pa-2">
            <div class="meta-box pa-3 rounded-xl text-center">
              <v-icon color="#58d388" class="mb-1">mdi-calendar-clock</v-icon>
              <div class="caption grey--text">Termin</div>
              <div class="body-2 font-weight-bold white--text mt-1">
                {{ formatLocalDate(selectedCourse.dateTime) }}
              </div>
              <div class="caption grey--text mt-0-5">{{ formatLocalTime(selectedCourse.dateTime) }} Uhr ({{ selectedCourse.duration }}m)</div>
            </div>
          </v-col>

          <v-col cols="6" class="pa-2">
            <div class="meta-box pa-3 rounded-xl text-center">
              <v-icon color="#58d388" class="mb-1">mdi-timer-sand</v-icon>
              <div class="caption grey--text">Warteliste</div>
              <div class="body-2 font-weight-bold white--text mt-1">
                {{ selectedCourse.waitlistEnabled ? `${selectedCourse.waitlistCount} Personen` : "Deaktiviert" }}
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Cancellation Box Warning -->
        <div class="notice-box pa-4 rounded-xl mb-6" :class="isDeadlinePassed(selectedCourse) ? 'border-red-notice' : 'border-gold-notice'">
          <div class="d-flex align-start">
            <v-icon :color="isDeadlinePassed(selectedCourse) ? 'red' : '#58d388'" class="mr-3 mt-1">
              {{ isDeadlinePassed(selectedCourse) ? "mdi-alert-circle-outline" : "mdi-alert-decagram-outline" }}
            </v-icon>
            <div>
              <h4 class="white--text font-weight-black subtitle-2 text-uppercase tracking-wider mb-1">Abmeldefrist (Cancellation Deadline)</h4>
              <p class="caption grey--text text--lighten-2 leading-relaxed mb-0" v-if="isDeadlinePassed(selectedCourse)">
                Die Abmeldefrist für diesen Kurs ist abgelaufen. Eine Abmeldung über diese App ist nicht mehr möglich. Falls du aus dringenden Gründen
                absagen musst, wende dich bitte direkt an deinen Vorgesetzten/Manager.
              </p>
              <p class="caption grey--text text--lighten-2 leading-relaxed mb-0" v-else>
                Du kannst dich bis zum <strong>{{ formatShortDateTime(selectedCourse.cancellationDeadline) }} Uhr</strong> abmelden. Danach wird die
                Buchung gesperrt und eine Stornierung ist nur noch für Manager möglich.
              </p>
            </div>
          </div>
        </div>

        <!-- Registration Actions in Dialog -->
        <div class="d-flex gap-3 justify-end">
          <v-btn text color="grey lighten-1" @click="detailDialog = false" class="rounded-lg">Schliessen</v-btn>

          <v-btn
            v-if="isUserRegistered(selectedCourse) || isUserWaitlisted(selectedCourse)"
            color="red lighten-1"
            class="black--text rounded-lg font-weight-bold px-6"
            :disabled="isDeadlinePassed(selectedCourse)"
            @click="confirmUnregistration(selectedCourse)">
            {{ isDeadlinePassed(selectedCourse) ? "Stornierung gesperrt" : "Vom Kurs abmelden" }}
          </v-btn>

          <v-btn
            v-else-if="hasAvailableSpots(selectedCourse)"
            color="#009640"
            class="white--text rounded-lg font-weight-bold px-6 primary-course-action"
            :loading="registeringId === selectedCourse._id"
            @click="registerForCourse(selectedCourse)">
            Anmelden
          </v-btn>

          <v-btn
            v-else-if="selectedCourse.waitlistEnabled"
            color="orange"
            class="black--text rounded-lg font-weight-bold px-6"
            :loading="registeringId === selectedCourse._id"
            @click="registerForCourse(selectedCourse)">
            Auf Warteliste eintragen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Confirm Unregistration Dialog -->
    <v-dialog v-model="confirmUnregDialog.show" max-width="400px" dark>
      <v-card class="dialog-surface pa-4 rounded-xl">
        <h3 class="text-h6 font-weight-bold white--text mb-2">Abmeldung bestätigen?</h3>
        <p class="grey--text caption leading-relaxed mb-4">
          Möchtest du dich wirklich vom Kurs <strong>{{ confirmUnregDialog.courseTitle }}</strong> abmelden? Falls du auf der Hauptliste stehst, rückt
          die nächste Person von der Warteliste automatisch nach.
        </p>
        <div class="d-flex justify-end gap-3">
          <v-btn text color="grey" @click="confirmUnregDialog.show = false">Abbrechen</v-btn>
          <v-btn
            color="red lighten-1"
            class="black--text font-weight-bold rounded-lg"
            :loading="confirmUnregDialog.loading"
            @click="executeUnregistration">
            Ja, abmelden
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </main>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"
import SecuredMedia from "@/views/akademie/SecuredMedia.vue"
import EmployeeCourseVideos from "./EmployeeCourseVideos.vue"

export default {
  name: "EmployeeCoursesRegistration",
  components: { MarketingToolHeader, SecuredMedia, EmployeeCourseVideos },
  data() {
    return {
      loading: true,
      courses: [],
      programLoading: true,
      programLoadId: 0,
      programError: "",
      programStudioFilter: null,
      programs: [],
      programDialog: false,
      selectedProgram: null,
      mediaDialog: false,
      selectedMediaBlock: null,
      downloadingMedia: false,
      studioFilter: "all",
      statusFilter: "all",
      selectedDateKey: "all",
      registeringId: null,

      detailDialog: false,
      selectedCourse: null,

      confirmUnregDialog: {
        show: false,
        courseId: null,
        courseTitle: null,
        loading: false,
      },
    }
  },
  watch: {
    activeStudioId() {
      this.closeProgram()
      this.selectedProgram = null
      this.programs = []
      this.fetchPrograms()
    },
    mediaDialog(isOpen) {
      if (!isOpen) {
        this.selectedMediaBlock = null
        this.downloadingMedia = false
      }
    },

    programDialog(isOpen) {
      if (isOpen) return
      this.mediaDialog = false
      this.selectedMediaBlock = null
    },

    // Reset selected date if filters change and make the previously selected day unavailable
    availableDays(newDays) {
      if (this.selectedDateKey !== "all" && !newDays.some((d) => d.dateString === this.selectedDateKey)) {
        this.selectedDateKey = "all"
      }
    },
  },
  computed: {
    canViewEmployeeCourses() {
      return userService.hasRight("gf_fetchEmployeeCourses", "gf_manageEmployeeCourses")
    },

    canManageEmployeeCourses() {
      return userService.hasRight("gf_manageEmployeeCourses")
    },

    canManagePrograms() {
      return userService.hasRight("gf_manageEmployeeCourseContent")
    },

    activeStudioId() {
      return this.$store.getters["studioStore/getSelectedStudioId"]
    },

    programStudioOptions() {
      const studios = new Map()
      this.programs.forEach((program) => (program.studios || []).forEach((studio) => studios.set(studio._id, studio)))
      return [...studios.values()].sort((left, right) => left.name.localeCompare(right.name, "de-CH"))
    },

    visiblePrograms() {
      return this.programs
        .filter((program) => !this.canManagePrograms || !this.programStudioFilter || !program.studios?.length || program.studios.some((studio) => studio._id === this.programStudioFilter))
        .sort((left, right) => new Date(right.validFrom) - new Date(left.validFrom))
    },

    selectedProgramContentBlocks() {
      if (!this.selectedProgram || !Array.isArray(this.selectedProgram.blocks)) return []
      return this.selectedProgram.blocks.filter((block) => block.type === "text" || block.type === "notice")
    },

    selectedProgramMediaBlocks() {
      if (!this.selectedProgram || !Array.isArray(this.selectedProgram.blocks)) return []
      return this.selectedProgram.blocks.filter((block) => block.type === "image" || block.type === "video")
    },

    availableCourseCount() {
      return this.courses.filter((course) => !this.isUserRegistered(course) && !this.isUserWaitlisted(course) && this.hasAvailableSpots(course))
        .length
    },

    myBookingsCount() {
      return this.courses.filter((course) => this.isUserRegistered(course)).length
    },

    myWaitlistCount() {
      return this.courses.filter((course) => this.isUserWaitlisted(course)).length
    },

    // Dynamic list of studios from loaded courses
    studioItems() {
      const items = [{ text: "Alle Standorte", value: "all" }]
      const map = new Map()

      this.courses.forEach((c) => {
        if (c.studio && c.studio._id) {
          map.set(c.studio._id.toString(), c.studio.name)
        }
      })

      map.forEach((name, id) => {
        items.push({ text: name, value: id })
      })

      return items
    },

    // Apply client-side filters (Studio & Status)
    filteredCourses() {
      let list = this.courses

      // 1. Studio filter
      if (this.studioFilter !== "all") {
        list = list.filter((c) => c.studio && c.studio._id.toString() === this.studioFilter)
      }

      // 2. Status filter
      if (this.statusFilter === "registered") {
        list = list.filter((c) => this.isUserRegistered(c) || this.isUserWaitlisted(c))
      } else if (this.statusFilter === "available") {
        list = list.filter((c) => !this.isUserRegistered(c) && !this.isUserWaitlisted(c) && this.hasAvailableSpots(c))
      } else if (this.statusFilter === "waitlisted") {
        list = list.filter((c) => this.isUserWaitlisted(c))
      }

      return list
    },

    // Extracts all unique calendar days that have courses based on active filters
    availableDays() {
      const daysMap = new Map()

      this.filteredCourses.forEach((course) => {
        if (!course.dateTime) return
        const d = new Date(course.dateTime)
        const dateString = d.toISOString().split("T")[0] // YYYY-MM-DD

        if (!daysMap.has(dateString)) {
          daysMap.set(dateString, {
            dateString,
            dateObj: d,
            dayName: d.toLocaleDateString("de-CH", { weekday: "short" }),
            dayNum: d.getDate(),
            monthName: d.toLocaleDateString("de-CH", { month: "short" }),
            hasBooking: false,
            hasWaitlist: false,
            coursesCount: 0,
          })
        }

        const dayInfo = daysMap.get(dateString)
        dayInfo.coursesCount++
        if (this.isUserRegistered(course)) dayInfo.hasBooking = true
        if (this.isUserWaitlisted(course)) dayInfo.hasWaitlist = true
      })

      return Array.from(daysMap.values()).sort((a, b) => a.dateObj - b.dateObj)
    },

    // Groups the filtered courses by local date (optionally filtered by calendar strip)
    groupedCourses() {
      const groups = {}
      this.filteredCourses.forEach((course) => {
        if (!course.dateTime) return
        const dateObj = new Date(course.dateTime)
        const dateKeyString = dateObj.toISOString().split("T")[0] // YYYY-MM-DD

        // Filter by calendar strip day if a specific day is selected
        if (this.selectedDateKey !== "all" && dateKeyString !== this.selectedDateKey) {
          return
        }

        const dateKey = dateObj.toLocaleDateString("de-CH", {
          weekday: "long",
          day: "2-digit",
          month: "long",
          year: "numeric",
        })

        if (!groups[dateKey]) {
          groups[dateKey] = []
        }
        groups[dateKey].push(course)
      })

      return Object.keys(groups).map((date) => ({
        date,
        courses: groups[date],
      }))
    },
  },
  async created() {
    const requests = [this.fetchPrograms()]
    if (this.canViewEmployeeCourses) requests.push(this.fetchData())
    else this.loading = false
    await Promise.all(requests)
  },
  beforeDestroy() {
    this.programLoadId += 1
  },
  methods: {
    async refreshPage() {
      const requests = [this.fetchPrograms()]
      if (this.$refs.courseVideos) requests.push(this.$refs.courseVideos.refresh())
      if (this.canViewEmployeeCourses) requests.push(this.fetchData())
      await Promise.all(requests)
    },

    async fetchPrograms() {
      const loadId = ++this.programLoadId
      this.programLoading = true
      this.programError = ""
      try {
        const programs = (await api.employeeCourses.programs.fetchPrograms()) || []
        if (loadId !== this.programLoadId) return
        this.programs = programs
        if (this.selectedProgram) {
          const updated = this.programs.find((program) => program._id === this.selectedProgram._id)
          if (updated) this.selectedProgram = updated
          else {
            this.closeProgram()
            this.selectedProgram = null
          }
        }
      } catch (error) {
        if (loadId !== this.programLoadId) return
        console.error(error)
        this.programs = []
        this.closeProgram()
        this.selectedProgram = null
        this.programError = "HYROX/Reformer Programme konnten nicht geladen werden. Bitte versuche es erneut."
      } finally {
        if (loadId === this.programLoadId) this.programLoading = false
      }
    },

    async fetchData() {
      if (!this.canViewEmployeeCourses) {
        this.loading = false
        return
      }
      this.loading = true
      try {
        const data = await api.employeeCourses.fetchCourses()
        this.courses = data || []

        // Refresh selected course details if dialog is open
        if (this.selectedCourse) {
          const updated = this.courses.find((c) => c._id === this.selectedCourse._id)
          if (updated) this.selectedCourse = updated
        }
      } catch (error) {
        console.error(error)
        this.$toast.error("Kurse konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },

    localDateKey(value) {
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return ""
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Zurich",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(date)
      const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))
      return `${values.year}-${values.month}-${values.day}`
    },

    programDateLabel(value) {
      if (!value) return "Kein Startdatum"
      return `Gültig ab ${new Date(value).toLocaleDateString("de-CH", {
        timeZone: "Europe/Zurich",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })}`
    },

    programStateLabel(program) {
      if (!program) return "Programm"
      if (this.localDateKey(program.validFrom) > this.localDateKey(new Date())) return "Demnächst"
      return "Programm"
    },

    openProgram(program) {
      this.selectedProgram = program
      this.mediaDialog = false
      this.selectedMediaBlock = null
      this.programDialog = true
    },

    closeProgram() {
      this.mediaDialog = false
      this.selectedMediaBlock = null
      this.programDialog = false
    },

    openProgramMedia(block) {
      this.selectedMediaBlock = block
      this.mediaDialog = true
    },

    programMediaNumber(block) {
      if (!this.selectedProgram || !Array.isArray(this.selectedProgram.blocks)) return 1
      const mediaBlocks = this.selectedProgram.blocks.filter((item) => item.type === block.type)
      const index = mediaBlocks.findIndex((item) => item._id === block._id)
      return index >= 0 ? index + 1 : 1
    },

    programMediaLabel(block) {
      return `${block.type === "video" ? "Video" : "Bild"} ${this.programMediaNumber(block)}`
    },

    programMediaTitle(block) {
      return block.title || `${block.type === "video" ? "Übungsvideo" : "Programmbild"} ${this.programMediaNumber(block)}`
    },

    programMediaDownloadName(block) {
      return (block && block.media && block.media.originalName) || `${this.programMediaTitle(block)}.jpg`
    },

    async downloadProgramImage() {
      if (!this.selectedProgram || !this.selectedMediaBlock || this.selectedMediaBlock.type !== "image" || this.downloadingMedia) return
      this.downloadingMedia = true
      try {
        await api.akademie.downloadFile(
          this.programMediaPath(this.selectedProgram, this.selectedMediaBlock),
          this.programMediaDownloadName(this.selectedMediaBlock),
        )
      } catch (error) {
        console.error(error)
        this.$toast.error("Das Bild konnte nicht heruntergeladen werden.")
      } finally {
        this.downloadingMedia = false
      }
    },

    programMediaPath(program, block) {
      if (!program || !block) return ""
      return block.type === "video"
        ? api.employeeCourses.programs.getPlaybackPath(program._id, block._id)
        : api.employeeCourses.programs.getMediaPath(program._id, block._id)
    },

    formatLocalDate(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("de-CH", {
        weekday: "short",
        day: "2-digit",
        month: "long",
      })
    },

    formatLocalTime(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleTimeString("de-CH", {
        hour: "2-digit",
        minute: "2-digit",
      })
    },

    formatShortDateTime(dateStr) {
      if (!dateStr) return ""
      return (
        new Date(dateStr).toLocaleDateString("de-CH", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }) + " Uhr"
      )
    },

    isDeadlinePassed(course) {
      if (!course || !course.cancellationDeadline) return true
      return new Date() >= new Date(course.cancellationDeadline)
    },

    isUserRegistered(course) {
      return course.userRegistration && course.userRegistration.status === "registered"
    },

    isUserWaitlisted(course) {
      return course.userRegistration && course.userRegistration.status === "waitlisted"
    },

    getUserWaitlistPos(course) {
      return course.userRegistration ? course.userRegistration.waitlistPosition : 0
    },

    hasAvailableSpots(course) {
      return course.registeredCount < course.capacity
    },

    getSpotsColor(course) {
      const free = course.capacity - course.registeredCount
      if (free <= 0) return "grey lighten-1"
      if (free <= 3) return "orange"
      return "#58d388"
    },

    getSpotsText(course) {
      const free = course.capacity - course.registeredCount
      if (free <= 0) {
        return course.waitlistEnabled ? "Warteliste aktiv" : "Ausgebucht"
      }
      return `${free} von ${course.capacity} Plätzen frei`
    },

    openCourseDetails(course) {
      this.selectedCourse = course
      this.detailDialog = true
    },

    async registerForCourse(course) {
      this.registeringId = course._id
      try {
        const res = await api.employeeCourses.register(course._id)
        this.$toast.success(res.message || "Erfolgreich angemeldet!")
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Anmeldung fehlgeschlagen.")
      } finally {
        this.registeringId = null
      }
    },

    confirmUnregistration(course) {
      const title = course.format ? course.format.title : "Mitarbeiterkurs"
      this.confirmUnregDialog = {
        show: true,
        courseId: course._id,
        courseTitle: title,
        loading: false,
      }
    },

    async executeUnregistration() {
      this.confirmUnregDialog.loading = true
      try {
        await api.employeeCourses.unregister(this.confirmUnregDialog.courseId)
        this.$toast.success("Erfolgreich abgemeldet.")
        this.confirmUnregDialog.show = false
        this.detailDialog = false
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Abmeldung fehlgeschlagen.")
      } finally {
        this.confirmUnregDialog.loading = false
      }
    },
  },
}
</script>

<style scoped>
.courses-shell {
  background-color: #0f0f0f;
  min-height: 100vh;
  color: white;
}

.courses-navbar {
  background-color: rgba(15, 15, 15, 0.75) !important;
  backdrop-filter: blur(15px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.courses-logo-bar {
  display: inline-block;
  width: 4px;
  height: 28px;
  background-color: #fbc02d;
  border-radius: 2px;
}

.uppercase-title {
  font-size: 0.875rem !important;
  letter-spacing: 2px !important;
  font-weight: 950 !important;
}

.uppercase-subtitle {
  font-size: 0.75rem !important;
  letter-spacing: 3px !important;
}

.courses-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Hero Banner */
.hero-banner {
  background: linear-gradient(135deg, #1c1c1e 0%, #0c0c0e 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.4);
}

.banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 80% 50%, rgba(251, 192, 45, 0.08) 0%, transparent 60%);
  pointer-events: none;
}

.banner-tag {
  background-color: #fbc02d;
  color: #000000;
  font-size: 0.75rem;
  letter-spacing: 1px;
}

.italic-title {
  font-style: italic;
  font-weight: 950;
  letter-spacing: -0.5px;
}

.responsive-hero-title {
  font-size: 2.625rem;
  line-height: 1.1;
}

@media (max-width: 600px) {
  .responsive-hero-title {
    font-size: 1.625rem !important;
    line-height: 1.2 !important;
  }
}

.max-width-text {
  max-width: 700px;
}

.leading-relaxed {
  line-height: 1.6;
}

.info-badge {
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

/* Filters */
.filters-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.courses-filter-select :deep(.v-input__control) {
  border-radius: 8px !important;
  background: rgba(255, 255, 255, 0.03) !important;
  background-color: rgba(255, 255, 255, 0.03) !important;
}

.courses-filter-select :deep(.v-input__slot) {
  background: rgba(255, 255, 255, 0.03) !important;
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.courses-filter-select :deep(.v-select__selection) {
  color: white !important;
}

.courses-filter-select :deep(.v-icon) {
  color: #fbc02d !important;
}

.active-chip {
  background-color: rgba(251, 192, 45, 0.1) !important;
  color: #fbc02d !important;
  border-color: #fbc02d !important;
}

/* Calendar Day Strip */
.calendar-strip-wrapper {
  overflow-x: auto;
  padding: 8px 4px 12px 4px;
  margin: 0 -4px;
  scrollbar-width: none; /* Firefox */
}

.calendar-strip-wrapper::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

.calendar-strip {
  display: inline-flex;
}

.calendar-day-card {
  width: 70px;
  height: 90px;
  flex-shrink: 0;
  background-color: #1c1c1e;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  color: #a0a0a5;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.calendar-day-card:hover {
  border-color: rgba(251, 192, 45, 0.4);
  transform: translateY(-2px);
}

.active-day-card {
  background: linear-gradient(135deg, #fbc02d 0%, #f9a825 100%) !important;
  border-color: #fbc02d !important;
  transform: none;
  box-shadow: 0 6px 20px rgba(251, 192, 45, 0.35) !important;
  color: #000000 !important;
}

.day-weekday {
  font-size: 0.75rem;
  letter-spacing: 1px;
  font-weight: 800;
}

.day-num {
  font-size: 1.375rem;
  line-height: 1.1;
  margin: 2px 0;
  color: white;
}

.active-day-card .day-num {
  color: #000000 !important;
}

.day-month {
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

/* Dots underneath the days indicating bookings */
.day-indicator-dots {
  position: absolute;
  bottom: 6px;
}

.indicator-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  display: inline-block;
}

.dot-green {
  background-color: #4caf50;
  box-shadow: 0 0 4px #4caf50;
}

.dot-orange {
  background-color: #ff9800;
  box-shadow: 0 0 4px #ff9800;
}

.active-day-card .dot-green {
  background-color: #1b5e20;
  box-shadow: none;
}

.active-day-card .dot-orange {
  background-color: #e65100;
  box-shadow: none;
}

/* Course Grouping Headers */
.border-bottom-header {
  border-bottom: 2px solid rgba(255, 255, 255, 0.08);
}

.date-header-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #fbc02d;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(251, 192, 45, 0.6);
}

/* Course Cards */
.course-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(251, 192, 45, 0.1) !important;
  border-color: rgba(251, 192, 45, 0.3) !important;
}

.border-registered {
  border-color: rgba(76, 175, 80, 0.4) !important;
  box-shadow: 0 8px 24px rgba(76, 175, 80, 0.05) !important;
}

.card-header-accent {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, transparent 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.divider-thin {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.06);
}

.uppercase-btn {
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 1px;
}

.register-glowing-btn {
  box-shadow: 0 0 15px rgba(251, 192, 45, 0.2);
}

.register-glowing-btn:hover {
  box-shadow: 0 0 25px rgba(251, 192, 45, 0.4);
}

/* Detail Card Modal */
.detail-card {
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5) !important;
}

.meta-box {
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  height: 100%;
}

.notice-box {
  background-color: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.border-gold-notice {
  border-left: 4px solid #fbc02d;
}

.border-red-notice {
  border-left: 4px solid #f50057;
}

/* Helper Utilities */
.width-100 {
  width: 100%;
}

.gap-1 {
  gap: 4px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-state {
  background-color: #1c1c1e;
  border: 1px dashed rgba(255, 255, 255, 0.1);
}

/* Academy-aligned page system */
.courses-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.courses-shell {
  width: 100%;
  max-width: 92.5rem;
  min-height: auto;
  margin: 0 auto;
  color: var(--text);
  background: transparent;
}

.courses-container {
  max-width: none;
  margin: 1rem auto 0;
}

.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

.metric-card {
  display: flex;
  min-height: 6.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
}

.metric-icon,
.section-icon {
  display: grid;
  flex: 0 0 2.625rem;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.metric-icon .v-icon,
.section-icon .v-icon {
  color: currentColor;
}

.icon-green,
.section-icon {
  color: #58d388;
  background: rgba(0, 150, 64, 0.13);
}

.icon-calendar {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.11);
}

.icon-waiting {
  color: #f4c95d;
  background: rgba(244, 201, 93, 0.11);
}

.metric-card > div {
  display: grid;
  min-width: 0;
}

.metric-card > div > span:first-child {
  color: var(--text-soft);
  font-size: 0.875rem;
  font-weight: 700;
}

.metric-card strong {
  color: #ffffff;
  font-size: 1.6875rem;
  line-height: 1.15;
}

.metric-card small {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.filters-card {
  padding: 1.125rem;
  border: 1px solid var(--border) !important;
  border-radius: 1.25rem !important;
  background: var(--surface) !important;
}

.filters-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.filters-heading > div {
  display: grid;
}

.filters-heading span:not(.section-icon) {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.filters-heading strong {
  margin-top: 0.125rem;
  color: #ffffff;
  font-size: 1rem;
}

.courses-filter-select ::v-deep .v-input__slot {
  min-height: 2.875rem !important;
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.025) !important;
}

.courses-filter-select ::v-deep .v-icon {
  color: #58d388 !important;
}

.status-filter-group {
  max-width: 100%;
}

.status-filter-group ::v-deep .v-slide-group__content {
  flex-wrap: nowrap;
  justify-content: flex-end;
}

.status-filter-group ::v-deep .v-chip {
  min-height: 2.75rem;
  padding: 0 0.875rem;
  border-color: var(--border) !important;
  color: var(--text-soft) !important;
  font-size: 0.875rem !important;
}

.status-filter-group ::v-deep .v-chip.active-chip {
  border-color: rgba(88, 211, 136, 0.5) !important;
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.16) !important;
}

.calendar-strip-wrapper {
  margin: 0;
  padding: 0.125rem 0 0.625rem;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-color: rgba(88, 211, 136, 0.35) transparent;
  scrollbar-width: thin;
  scroll-snap-type: x proximity;
}

.calendar-strip {
  display: flex;
  gap: 0.625rem;
}

.calendar-day-card {
  width: 4.75rem;
  min-width: 4.75rem;
  height: 5.75rem;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  outline: none;
  color: var(--muted);
  background: var(--surface);
  scroll-snap-align: start;
  transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease, transform 160ms ease;
}

.calendar-day-card:focus-visible {
  border-color: rgba(88, 211, 136, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.active-day-card {
  border-color: rgba(88, 211, 136, 0.55) !important;
  color: #ffffff !important;
  background: linear-gradient(145deg, rgba(0, 150, 64, 0.38), rgba(0, 150, 64, 0.16)) !important;
  box-shadow: 0 0.75rem 1.75rem rgba(0, 0, 0, 0.2) !important;
  transform: none;
}

.day-weekday,
.day-month {
  font-size: 0.75rem;
  line-height: 1.2;
  letter-spacing: 0.04em;
}

.day-num {
  margin: 0.125rem 0;
  color: #ffffff;
  font-size: 1.375rem;
  line-height: 1.1;
}

.active-day-card .day-num {
  color: #ffffff !important;
}

.date-header-dot {
  background-color: #58d388;
  box-shadow: 0 0 0.625rem rgba(88, 211, 136, 0.42);
}

.border-bottom-header {
  border-bottom: 1px solid var(--border);
}

.course-card {
  border: 1px solid var(--border) !important;
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
  box-shadow: none !important;
  transition: border-color 160ms ease, background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease;
}

.course-card:focus-within {
  border-color: var(--border-strong) !important;
}

.card-header-accent {
  min-height: 4.125rem;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.095), rgba(255, 255, 255, 0.025));
  border-bottom: 1px solid rgba(255, 255, 255, 0.055);
}

.border-registered {
  border-color: rgba(88, 211, 136, 0.42) !important;
  box-shadow: inset 0 0 0 1px rgba(88, 211, 136, 0.08) !important;
}

.courses-page ::v-deep .v-chip.v-size--small,
.courses-page ::v-deep .v-chip.v-size--x-small {
  min-height: 1.75rem;
  font-size: 0.75rem !important;
}

.courses-page .yellow--text.text--darken-2 {
  color: #58d388 !important;
}

.course-card .caption,
.detail-card .caption,
.empty-state .caption {
  font-size: 0.75rem !important;
}

.primary-course-action,
.course-card .v-btn,
.detail-card .v-btn {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  letter-spacing: 0;
  text-transform: none;
}

.register-glowing-btn,
.primary-course-action {
  box-shadow: none;
}

.dialog-surface {
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  border: 1px solid var(--border) !important;
  color: var(--text);
  background: #1a1a1c !important;
  box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.42) !important;
}

.detail-card {
  border-radius: 1.375rem !important;
}

.meta-box,
.notice-box {
  border-color: var(--border);
  background: rgba(255, 255, 255, 0.03);
}

.border-gold-notice {
  border-left-color: #58d388;
}

.empty-state {
  border: 1px dashed var(--border);
  border-radius: 1.25rem !important;
  background: var(--surface);
}

.program-showcase {
  width: 100%;
  margin: 1rem 0;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: var(--surface);
}

.program-showcase-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.875rem;
}

.employee-courses-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.5rem 0 0.875rem;
}

.employee-courses-heading > div {
  min-width: 0;
}

.employee-courses-heading > div > span {
  display: block;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  line-height: 1.4;
  text-transform: uppercase;
}

.employee-courses-heading h2 {
  margin: 0.125rem 0 0.1875rem;
  color: #ffffff;
  font-size: 1.375rem;
  line-height: 1.3;
}

.employee-courses-heading p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.employee-courses-manage-action {
  min-height: 2.625rem;
  flex: 0 0 auto;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.program-showcase-heading > div {
  min-width: 0;
}

.program-showcase-heading > div > span,
.program-detail-header small,
.program-video-dialog-header small {
  display: block;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  line-height: 1.4;
  text-transform: uppercase;
}

.program-showcase-heading h2 {
  margin: 0.125rem 0 0.1875rem;
  color: #fff;
  font-size: 1.375rem;
  line-height: 1.3;
}

.program-showcase-heading p,
.program-empty p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.program-manage-action,
.program-empty .v-btn {
  min-height: 2.625rem;
  flex: 0 0 auto;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.program-loading,
.program-empty {
  display: flex;
  min-height: 7.5rem;
  align-items: center;
  justify-content: center;
  gap: 0.875rem;
  padding: 1rem;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 1rem;
  color: #a3a3a8;
  font-size: 0.875rem;
}

.program-empty {
  justify-content: flex-start;
}

.program-empty > span,
.program-tile-icon,
.program-detail-icon,
.program-video-icon,
.program-video-dialog-icon,
.program-section-icon,
.program-document-icon,
.program-copy-icon,
.program-media-dialog-icon,
.program-block-heading > span,
.program-media-heading > span,
.notice-icon {
  display: inline-flex;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.16);
}

.program-empty > div {
  min-width: 0;
  flex: 1;
}

.program-empty strong {
  display: block;
  color: #f4f4f5;
  font-size: 1rem;
}

.program-studio-filter {
  max-width: 28rem;
  margin-bottom: 1.25rem;
}

.program-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.program-tile {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid rgba(88, 211, 136, 0.28);
  border-radius: 1rem;
  color: #f4f4f5;
  background: #202321;
  text-align: left;
  transition: border-color 160ms ease, background-color 160ms ease;
}

.program-tile:focus-visible {
  outline: 2px solid #58d388;
  outline-offset: 2px;
}

.program-tile-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.program-tile-copy {
  min-width: 0;
}

.program-tile-copy small {
  display: block;
  color: #7ee5a6;
  font-size: 0.75rem;
  line-height: 1.5;
}

.program-tile-copy strong {
  display: block;
  margin-top: 0.25rem;
  overflow-wrap: anywhere;
  color: #fff;
  font-size: 1.25rem;
  line-height: 1.4;
}

.program-tile-description {
  color: #c7c7ca;
  font-size: 1rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.program-studios {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.program-studios > span {
  max-width: 100%;
  padding: 0.25rem 0.625rem;
  border-radius: 0.5rem;
  color: #b0e6c4;
  background: rgba(88, 211, 136, 0.1);
  font-size: 0.75rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.program-detail-body > .program-studios {
  margin-bottom: 1.25rem;
}

.program-tile-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  color: #b5b5b9;
  font-size: 0.875rem;
}

.program-tile-meta > span {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
}

.program-tile-meta .v-icon {
  color: inherit;
  font-size: 1rem;
}

.program-tile-action {
  color: #7ee5a6;
  font-weight: 700;
}

.program-detail-card,
.program-video-dialog-card,
.program-media-dialog-card {
  overflow: hidden;
  border: 1px solid rgba(88, 211, 136, 0.22) !important;
  border-radius: 1.25rem !important;
  color: #f4f4f5;
  background: #171918 !important;
  font-size: 1rem;
}

.program-detail-header,
.program-video-dialog-header,
.program-media-dialog-header {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(135deg, rgba(0, 150, 64, 0.16), rgba(23, 25, 24, 0.98) 52%);
}

.program-detail-header > div,
.program-video-dialog-header > div,
.program-media-dialog-header > div:not(.program-media-dialog-actions) {
  min-width: 0;
  flex: 1;
}

.program-detail-header h2,
.program-video-dialog-header h2,
.program-media-dialog-header h2 {
  margin: 0.1875rem 0 0;
  overflow-wrap: anywhere;
  color: #fff;
  font-size: 1.5rem;
  line-height: 1.3;
}

.program-detail-body {
  padding: 1.25rem !important;
}

.program-overview {
  padding: 1rem;
  border: 1px solid rgba(88, 211, 136, 0.2);
  border-radius: 1rem;
  background: rgba(0, 150, 64, 0.08);
}

.program-overview > span,
.program-section-heading small,
.program-copy-block small,
.program-document-copy small,
.program-media-dialog-header small,
.program-media-description > span {
  display: block;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  line-height: 1.4;
  text-transform: uppercase;
}

.program-overview p {
  margin: 0.375rem 0 0;
  color: #d4d4d8;
  font-size: 1rem;
  line-height: 1.65;
  white-space: pre-line;
}

.program-detail-section {
  margin-top: 1.25rem;
}

.program-section-heading,
.program-section-heading > div,
.program-media-dialog-actions {
  display: flex;
  align-items: center;
}

.program-section-heading {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.program-section-heading > div {
  min-width: 0;
  gap: 0.75rem;
}

.program-section-heading h3 {
  margin: 0.125rem 0 0;
  color: #ffffff;
  font-size: 1.25rem;
  line-height: 1.35;
}

.program-section-heading > span {
  display: inline-flex;
  min-width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  color: #c8f5d9;
  background: rgba(0, 150, 64, 0.14);
  font-size: 0.75rem;
  font-weight: 850;
}

.program-content-list,
.program-document-list {
  display: grid;
  gap: 0.625rem;
}

.program-copy-block,
.program-document-row {
  min-width: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #202220;
}

.program-copy-block {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem;
}

.program-copy-block.is-notice {
  border-color: rgba(242, 183, 5, 0.28);
  background: rgba(242, 183, 5, 0.07);
}

.program-copy-block.is-notice .program-copy-icon {
  color: #ffdb66;
  background: rgba(242, 183, 5, 0.14);
}

.program-copy-block > div,
.program-document-copy {
  min-width: 0;
}

.program-copy-block h4,
.program-document-copy h4 {
  margin: 0.1875rem 0 0;
  overflow-wrap: anywhere;
  color: #ffffff;
  font-size: 1.0625rem;
  line-height: 1.4;
}

.program-copy-block p,
.program-document-copy p {
  margin: 0.375rem 0 0;
  color: #d0d0d3;
  font-size: 0.875rem;
  line-height: 1.6;
  white-space: pre-line;
}

.program-document-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
}

.program-document-icon.is-image {
  color: #8ed8ff;
  background: rgba(35, 151, 214, 0.14);
}

.program-document-copy p.is-empty {
  color: #8f8f95;
  font-style: italic;
}

.program-document-action {
  min-height: 2.75rem;
  flex: 0 0 auto;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.program-media-dialog-card {
  display: flex;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
}

.program-media-dialog-header {
  flex: 0 0 auto;
}

.program-media-dialog-actions {
  flex: 0 0 auto;
  gap: 0.25rem;
}

.program-media-dialog-body {
  overflow-y: auto;
  padding: 1rem !important;
}

.program-media-viewer {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #080908;
}

.program-media-viewer.is-video {
  overflow: hidden;
}

.program-media-viewer.is-video ::v-deep .secured-media-wrapper,
.program-media-viewer.is-video ::v-deep .secured-media-content.video {
  border: 0;
  border-radius: 0;
}

.program-media-viewer.is-image {
  overflow: visible;
  padding: 0.75rem;
}

.program-media-viewer.is-image ::v-deep .secured-media-wrapper {
  min-height: 16rem;
}

.program-media-viewer.is-image ::v-deep .secured-media-content.image {
  width: 100%;
  height: auto;
  max-height: none;
  object-fit: contain;
}

.program-media-description {
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #202220;
}

.program-media-description p {
  margin: 0.375rem 0 0;
  color: #d0d0d3;
  font-size: 1rem;
  line-height: 1.65;
  white-space: pre-line;
}

.program-intro {
  margin: 0 0 1rem;
  padding: 1rem;
  border-left: 3px solid #58d388;
  border-radius: 0 0.75rem 0.75rem 0;
  color: #d4d4d8;
  background: rgba(0, 150, 64, 0.09);
  font-size: 1rem;
  line-height: 1.65;
  white-space: pre-line;
}

.program-blocks {
  display: grid;
  gap: 0.875rem;
}

.program-block {
  min-width: 0;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #202220;
}

.program-block-heading,
.program-media-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.program-block-heading > span,
.program-media-heading > span,
.notice-icon {
  width: 2.375rem;
  height: 2.375rem;
  flex-basis: 2.375rem;
}

.program-block h3 {
  margin: 0;
  overflow-wrap: anywhere;
  color: #fff;
  font-size: 1.125rem;
  line-height: 1.4;
}

.program-block > p,
.program-block-notice p,
.program-media-caption {
  margin: 0;
  color: #d0d0d3;
  font-size: 1rem;
  line-height: 1.65;
  white-space: pre-line;
}

.program-block-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  border-color: rgba(242, 183, 5, 0.3);
  background: rgba(242, 183, 5, 0.08);
}

.program-block-notice .notice-icon {
  color: #ffdb66;
  background: rgba(242, 183, 5, 0.14);
}

.program-block-notice > div {
  min-width: 0;
  flex: 1;
}

.program-block-notice h3 {
  margin-bottom: 0.25rem;
}

.program-block-video {
  border-color: rgba(88, 211, 136, 0.24);
  background: linear-gradient(135deg, rgba(0, 150, 64, 0.08), #202220 48%);
}

.program-video-summary {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
}

.program-video-icon,
.program-video-dialog-icon {
  color: #7ee5a6;
}

.program-video-copy {
  min-width: 0;
}

.program-video-copy small {
  display: block;
  margin-bottom: 0.1875rem;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  line-height: 1.4;
  text-transform: uppercase;
}

.program-video-copy p {
  display: -webkit-box;
  margin: 0.25rem 0 0;
  overflow: hidden;
  color: #b8b8bc;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-line;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.program-video-action {
  min-height: 2.75rem;
  flex: 0 0 auto;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.program-video-dialog-body {
  padding: 1.25rem !important;
}

.program-video-player {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #080908;
}

.program-video-player ::v-deep .secured-media-wrapper {
  border: 0;
}

.program-video-player ::v-deep .secured-media-content.video {
  border-radius: 0;
}

.program-video-description {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #202220;
}

.program-video-description span {
  display: block;
  margin-bottom: 0.375rem;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  line-height: 1.4;
  text-transform: uppercase;
}

.program-video-description p {
  margin: 0;
  color: #d0d0d3;
  font-size: 1rem;
  line-height: 1.65;
  white-space: pre-line;
}

.program-media-frame {
  overflow: hidden;
  border-radius: 0.75rem;
  background: #0e100f;
}

.program-media-frame ::v-deep .secured-media-wrapper {
  min-height: 18rem;
  border: 0;
  border-radius: 0;
}

.program-media-frame ::v-deep .secured-media-content.image {
  max-height: 38rem;
  object-fit: contain;
}

.program-media-caption {
  margin-top: 0.75rem;
}

@media (hover: hover) and (pointer: fine) {
  .program-tile:hover {
    border-color: rgba(88, 211, 136, 0.5);
    background: #252826;
    transform: translateY(-2px);
  }

  .calendar-day-card:hover {
    border-color: var(--border-strong);
    background: var(--surface-strong);
    transform: translateY(-2px);
  }

  .course-card:hover {
    border-color: var(--border-strong) !important;
    background: var(--surface-strong) !important;
    box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.16) !important;
    transform: translateY(-2px);
  }
}

@media (max-width: 78.9375rem) {
  .courses-page {
    padding: 1.125rem;
  }
}

@media (max-width: 59.9375rem) {
  .program-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .status-filter-group ::v-deep .v-slide-group__content {
    justify-content: flex-start;
  }
}

@media (max-width: 37.4375rem) {
  .courses-page {
    padding: 0.75rem;
  }

  .program-showcase {
    margin: 0.75rem 0;
    padding: 0.75rem;
    border-radius: 1rem;
  }

  .program-showcase-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .program-showcase-heading h2 {
    font-size: 1.25rem;
  }

  .program-manage-action {
    width: 100%;
  }

  .program-video-summary {
    grid-template-columns: 2.5rem minmax(0, 1fr);
    align-items: flex-start;
    gap: 0.75rem;
  }

  .program-video-icon {
    width: 2.5rem;
    height: 2.5rem;
    flex-basis: 2.5rem;
  }

  .program-video-action {
    grid-column: 1 / -1;
    width: 100%;
  }

  .program-video-dialog-card {
    min-height: 100vh;
    min-height: 100dvh;
    border: 0 !important;
    border-radius: 0 !important;
  }

  .program-media-dialog-card {
    min-height: 100vh;
    min-height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border: 0 !important;
    border-radius: 0 !important;
  }

  .program-video-dialog-header {
    gap: 0.75rem;
    padding: 1rem;
  }

  .program-media-dialog-header {
    gap: 0.625rem;
    padding: 1rem;
  }

  .program-media-dialog-icon {
    display: none;
  }

  .program-video-dialog-header h2 {
    font-size: 1.25rem;
  }

  .program-media-dialog-header h2 {
    font-size: 1.25rem;
  }

  .program-video-dialog-body {
    padding: 0.75rem !important;
  }

  .program-media-dialog-body {
    padding: 0.75rem !important;
  }

  .employee-courses-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .employee-courses-heading h2 {
    font-size: 1.25rem;
  }

  .employee-courses-manage-action {
    width: 100%;
  }

  .program-tile {
    padding: 0.875rem;
  }

  .program-empty {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .program-empty .v-btn {
    width: 100%;
  }

  .program-detail-card {
    min-height: 100vh;
    min-height: 100dvh;
    border: 0 !important;
    border-radius: 0 !important;
  }

  .program-detail-header,
  .program-detail-body {
    padding: 1rem !important;
  }

  .program-detail-icon {
    display: none;
  }

  .program-detail-header h2 {
    font-size: 1.25rem;
  }

  .program-section-heading {
    align-items: flex-start;
  }

  .program-section-icon {
    width: 2.5rem;
    height: 2.5rem;
    flex-basis: 2.5rem;
  }

  .program-document-row {
    grid-template-columns: 2.5rem minmax(0, 1fr);
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.875rem;
  }

  .program-document-icon,
  .program-copy-icon {
    width: 2.5rem;
    height: 2.5rem;
    flex-basis: 2.5rem;
  }

  .program-document-action {
    width: 100%;
    grid-column: 1 / -1;
  }

  .program-intro,
  .program-block > p,
  .program-block-notice p,
  .program-media-caption {
    font-size: 1rem;
  }

  .program-media-frame ::v-deep .secured-media-wrapper {
    min-height: 12rem;
  }

  .metric-grid {
    gap: 0.5rem;
    margin: 0.75rem 0;
  }

  .metric-card {
    min-height: 7rem;
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border-radius: 0.9375rem !important;
  }

  .metric-icon {
    width: 2.25rem;
    height: 2.25rem;
    flex-basis: 2.25rem;
  }

  .filters-card {
    padding: 0.875rem;
    border-radius: 1.0625rem !important;
  }

  .status-filter-group {
    width: 100%;
  }

  .calendar-day-card {
    width: 4.375rem;
    min-width: 4.375rem;
  }

  .courses-grid {
    margin-right: -0.375rem;
    margin-left: -0.375rem;
  }

  .courses-grid > .col {
    padding: 0.375rem;
  }

  .course-card .v-card__text {
    padding: 1rem !important;
  }

  .detail-card {
    min-height: 100vh;
    min-height: 100dvh;
    padding: 1rem !important;
    border: 0 !important;
    border-radius: 0 !important;
  }

  .detail-card > .d-flex.gap-3.justify-end {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .detail-card > .d-flex.gap-3.justify-end .v-btn {
    width: 100%;
    margin: 0;
  }
}

@media (max-width: 22.5rem) {
  .metric-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .metric-card {
    min-height: 5.75rem;
    align-items: center;
    flex-direction: row;
  }
}
</style>
